import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  ORDER_PDF_POLL_INTERVAL_MILLISECONDS,
  OrderPdfDownloadUrlResult,
  OrderPdfStatus,
  OrderService,
  UserMessage,
  UserMessageAction,
  formatUserMessageText,
  logOrderDevDetail,
  normalizePdfStatus,
  resolveOrderGraphqlUserMessage,
  resolveUserMessage,
  triggerAttachmentDownload,
} from 'src/app/entities/order';

/**
 * Injected configuration payload for OrderPdfDialogComponent.
 */
export interface OrderPdfDialogData {
  orderId: string;
  orderNumber?: string | number | null;
  pdfStatus?: string | null;
  lastPdfError?: string | null;
}

/**
 * Result returned upon closing the OrderPdfDialogComponent dialog.
 */
export interface OrderPdfDialogResult {
  pdfStatus?: string;
  pdfGeneratedAt?: string | null;
  lastPdfError?: string | null;
}

export type OrderPdfViewState = 'loading' | 'generating' | 'view' | 'error';

/**
 * Modal dialog for previewing, polling, and downloading Order of Service PDF documents.
 */
@Component({
  selector: 'app-order-pdf-dialog',
  standalone: true,
  templateUrl: './order-pdf-dialog.component.html',
  styleUrls: ['./order-pdf-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    TablerIconsModule,
  ],
  host: { class: 'order-pdf-dialog-host' },
})
export class OrderPdfDialogComponent implements OnInit, OnDestroy {
  public viewState: OrderPdfViewState = 'loading';
  public displayedErrorTitleText: string | null = null;
  public displayedErrorMessageText: string | null = null;
  public activeErrorActionType: UserMessageAction = 'none';

  public isDownloadingPdf: boolean = false;
  public isRetryingPdfGeneration: boolean = false;

  public sanitizedPdfResourceUrl: SafeResourceUrl | null = null;
  private activePollingIntervalTimer: ReturnType<typeof setInterval> | null =
    null;

  /**
   * Initializes OrderPdfDialogComponent with injected services and dialog data.
   *
   * Description:
   *   Injects MatDialogRef, modal data, OrderService, DomSanitizer, and MatSnackBar.
   *
   * Parameters:
   *   dialogReference: MatDialogRef<OrderPdfDialogComponent> - Modal control reference.
   *   injectedModalData: OrderPdfDialogData - Order metadata including orderId and status.
   *   orderServiceInstance: OrderService - Order API service.
   *   domSanitizerInstance: DomSanitizer - Angular DOM sanitizer for secure iframe rendering.
   *   snackBarNotificationService: MatSnackBar - Snackbar service for toast notifications.
   *
   * Return Value:
   *   OrderPdfDialogComponent - Initialized component instance.
   *
   * Exceptions & Errors:
   *   None.
   */
  constructor(
    private readonly dialogReference: MatDialogRef<OrderPdfDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public readonly injectedModalData: OrderPdfDialogData,
    private readonly orderServiceInstance: OrderService,
    private readonly domSanitizerInstance: DomSanitizer,
    private readonly snackBarNotificationService: MatSnackBar,
  ) {}

  /**
   * Angular Lifecycle hook executed on component initialization.
   *
   * Description:
   *   Initiates PDF URL fetch workflow.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public ngOnInit(): void {
    this.initiatePdfFetchWorkflow();
  }

  /**
   * Angular Lifecycle cleanup hook.
   *
   * Description:
   *   Clears active polling timers.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public ngOnDestroy(): void {
    this.stopPollingTimer();
  }

  /**
   * Resolves label for header display (e.g. "N° 1024" or "Orden de Servicio").
   *
   * Description:
   *   Formats order number string.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Human-readable label.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get orderLabel(): string {
    const orderNumberValue = this.injectedModalData.orderNumber;
    return orderNumberValue != null && String(orderNumberValue).trim()
      ? `N° ${orderNumberValue}`
      : 'Orden de Servicio';
  }

  public get showRetryAction(): boolean {
    return this.viewState === 'error' && this.activeErrorActionType === 'retry';
  }

  public get showRefreshAction(): boolean {
    return (
      this.viewState === 'error' && this.activeErrorActionType === 'refresh'
    );
  }

  /**
   * Triggers download of the PDF file with attachment disposition.
   *
   * Description:
   *   Queries presigned URL and dispatches browser download via triggerAttachmentDownload.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Catches GraphQL errors and shows snackbar error notification.
   */
  public downloadPdf(): void {
    if (this.isDownloadingPdf) {
      return;
    }

    this.isDownloadingPdf = true;

    this.orderServiceInstance
      .createOrderPdfDownloadUrl(this.injectedModalData.orderId, 'attachment')
      .subscribe({
        next: (downloadResultPayload: OrderPdfDownloadUrlResult) => {
          this.isDownloadingPdf = false;

          if (
            normalizePdfStatus(downloadResultPayload?.pdfStatus) ===
              OrderPdfStatus.GENERATED &&
            downloadResultPayload?.url
          ) {
            triggerAttachmentDownload(downloadResultPayload.url);
            this.snackBarNotificationService.open(
              'Descarga iniciada con éxito',
              'Cerrar',
              { duration: 3000 },
            );
            return;
          }

          if (
            normalizePdfStatus(downloadResultPayload?.pdfStatus) ===
            OrderPdfStatus.FAILED
          ) {
            this.logPdfDiagnosticDetail(downloadResultPayload);
            const userErrorMessage = this.resolvePdfFailureMessage(
              downloadResultPayload,
            );
            this.snackBarNotificationService.open(
              `${userErrorMessage.title}: ${formatUserMessageText(userErrorMessage)}`,
              'Cerrar',
              { duration: 4000 },
            );
            return;
          }

          this.snackBarNotificationService.open(
            'El PDF aún se encuentra en proceso de generación',
            'Cerrar',
            { duration: 3000 },
          );
        },
        error: (downloadErrorPayload: unknown) => {
          this.isDownloadingPdf = false;
          const userErrorMessage = resolveOrderGraphqlUserMessage(
            downloadErrorPayload,
            'No se pudo descargar el archivo PDF.',
          );
          this.snackBarNotificationService.open(
            formatUserMessageText(userErrorMessage),
            'Cerrar',
            { duration: 4000 },
          );
        },
      });
  }

  /**
   * Closes the modal returning updated PDF status metadata if available.
   *
   * Description:
   *   Builds OrderPdfDialogResult and closes dialogReference.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public close(): void {
    const normalizedCurrentStatus = normalizePdfStatus(
      this.injectedModalData.pdfStatus ?? null,
    );
    const dialogResultPayload: OrderPdfDialogResult | undefined =
      normalizedCurrentStatus &&
      normalizedCurrentStatus !== OrderPdfStatus.PENDING &&
      normalizedCurrentStatus !== OrderPdfStatus.PROCESSING
        ? {
            pdfStatus: normalizedCurrentStatus,
            pdfGeneratedAt:
              normalizedCurrentStatus === OrderPdfStatus.GENERATED
                ? new Date().toISOString()
                : null,
            lastPdfError: this.injectedModalData.lastPdfError ?? null,
          }
        : undefined;

    this.dialogReference.close(dialogResultPayload);
  }

  /**
   * Retries generating the PDF document when in FAILED state.
   *
   * Description:
   *   Invokes retryGenerateOrderPdf and restarts fetch loop.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Handles retry mutation rejections.
   */
  public retry(): void {
    if (this.isRetryingPdfGeneration) {
      return;
    }

    if (this.activeErrorActionType === 'refresh') {
      this.initiatePdfFetchWorkflow();
      return;
    }

    this.isRetryingPdfGeneration = true;
    this.viewState = 'loading';
    this.displayedErrorTitleText = null;
    this.displayedErrorMessageText = null;
    this.activeErrorActionType = 'none';
    this.sanitizedPdfResourceUrl = null;
    this.stopPollingTimer();

    this.orderServiceInstance
      .retryGenerateOrderPdf(this.injectedModalData.orderId)
      .subscribe({
        next: (retryResultPayload) => {
          this.isRetryingPdfGeneration = false;
          this.injectedModalData.pdfStatus = retryResultPayload.pdfStatus;
          this.injectedModalData.lastPdfError = null;
          this.initiatePdfFetchWorkflow();
        },
        error: (retryErrorPayload: unknown) => {
          this.isRetryingPdfGeneration = false;
          const userErrorMessage = resolveOrderGraphqlUserMessage(
            retryErrorPayload,
            'No se pudo reintentar la generación del documento PDF.',
          );
          this.presentUserFacingError(userErrorMessage);
        },
      });
  }

  private initiatePdfFetchWorkflow(): void {
    this.viewState = 'loading';
    this.displayedErrorTitleText = null;
    this.displayedErrorMessageText = null;
    this.activeErrorActionType = 'none';
    this.requestInlinePdfDownloadUrl();
  }

  private requestInlinePdfDownloadUrl(): void {
    this.orderServiceInstance
      .createOrderPdfDownloadUrl(this.injectedModalData.orderId, 'inline')
      .subscribe({
        next: (resultPayload: OrderPdfDownloadUrlResult) =>
          this.handlePdfResponsePayload(resultPayload),
        error: (requestErrorPayload: unknown) =>
          this.handleRequestFailure(requestErrorPayload),
      });
  }

  private handlePdfResponsePayload(
    resultPayload: OrderPdfDownloadUrlResult | null | undefined,
  ): void {
    if (!resultPayload) {
      this.handleRequestFailure();
      return;
    }

    this.injectedModalData.pdfStatus = resultPayload.pdfStatus;
    this.injectedModalData.lastPdfError = resultPayload.lastPdfError ?? null;

    const normalizedStatus = normalizePdfStatus(resultPayload.pdfStatus);

    switch (normalizedStatus) {
      case OrderPdfStatus.PENDING:
      case OrderPdfStatus.PROCESSING:
        this.viewState = 'generating';
        this.startPollingTimer();
        return;

      case OrderPdfStatus.GENERATED:
        if (resultPayload.url) {
          this.renderPdfInViewer(resultPayload.url);
          return;
        }
        this.presentUserFacingError(
          resolveUserMessage(
            null,
            [],
            'El PDF está listo pero no se recibió la URL de descarga.',
          ),
        );
        return;

      case OrderPdfStatus.FAILED:
        this.logPdfDiagnosticDetail(resultPayload);
        this.presentUserFacingError(
          this.resolvePdfFailureMessage(resultPayload),
        );
        return;

      default:
        this.presentUserFacingError(
          resolveUserMessage(
            null,
            [],
            'Estado del PDF desconocido.',
          ),
        );
    }
  }

  private resolvePdfFailureMessage(
    resultPayload: OrderPdfDownloadUrlResult,
  ): UserMessage {
    return resolveUserMessage(
      resultPayload.errorCode ?? 'PDF_GENERATION_FAILED',
      [],
      undefined,
      resultPayload.userMessage,
    );
  }

  private logPdfDiagnosticDetail(
    resultPayload: OrderPdfDownloadUrlResult,
  ): void {
    logOrderDevDetail('pdf.lastPdfError', resultPayload.lastPdfError);
    logOrderDevDetail('pdf.errorCode', resultPayload.errorCode);
  }

  private renderPdfInViewer(fileUrlString: string): void {
    this.stopPollingTimer();
    this.sanitizedPdfResourceUrl =
      this.domSanitizerInstance.bypassSecurityTrustResourceUrl(fileUrlString);
    this.viewState = 'view';
  }

  private presentUserFacingError(userMessage: UserMessage): void {
    this.stopPollingTimer();
    this.displayedErrorTitleText = userMessage.title;
    this.displayedErrorMessageText = formatUserMessageText(userMessage);
    this.activeErrorActionType = userMessage.action ?? 'none';
    this.viewState = 'error';
  }

  private handleRequestFailure(encounteredError?: unknown): void {
    this.stopPollingTimer();
    const userErrorMessage = resolveOrderGraphqlUserMessage(
      encounteredError,
      'No se pudo consultar el documento PDF.',
    );
    this.presentUserFacingError(userErrorMessage);
  }

  private startPollingTimer(): void {
    if (this.activePollingIntervalTimer != null) {
      return;
    }

    this.activePollingIntervalTimer = setInterval(() => {
      this.requestInlinePdfDownloadUrl();
    }, ORDER_PDF_POLL_INTERVAL_MILLISECONDS);
  }

  private stopPollingTimer(): void {
    if (this.activePollingIntervalTimer != null) {
      clearInterval(this.activePollingIntervalTimer);
      this.activePollingIntervalTimer = null;
    }
  }
}
